import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { loadSQL, SCHEMA, all, one } from "@/lib/sqlite";
import * as V from "@/lib/vault";
import * as I from "@/lib/identity";
import { connectRelay } from "@/lib/relay";
import { isNative } from "@/lib/native";

const Ctx = createContext(null);
export const useVault = () => useContext(Ctx);

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const TTL = 604800;
const AUTO_LOCK_MS = 120000;
const clampTimer = (t) => Math.max(0, Math.min(Number(t) || 0, 604800));
const SNAP_DURATIONS = [3, 5, 10];
const clampSnap = (d) => (SNAP_DURATIONS.includes(Number(d)) ? Number(d) : 5);
const STORY_MS = 24 * 3600 * 1000;
export const REACTIONS = ["🔥", "😂", "😮", "❤️", "👏", "🚀"];
const REACT_TIMER = 10;
const MIGRATIONS = ["ALTER TABLE contacts ADD COLUMN trust TEXT DEFAULT 'accepted'", "ALTER TABLE sessions ADD COLUMN pending INTEGER DEFAULT 0", "ALTER TABLE profile ADD COLUMN push_on INTEGER DEFAULT 0"];
const SESSION_COLS = "INSERT INTO sessions (session_id, kind, name, recipient_code, members, self_destruct_timer, last_at, unread, created_at, pending) VALUES (?,?,?,?,?,?,?,?,?,?)";
const pushSupported = () => !isNative() && "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
const b64ToBytes = (b) => { const s = atob((b + "=".repeat((4 - (b.length % 4)) % 4)).replace(/-/g, "+").replace(/_/g, "/")); return Uint8Array.from(s, (c) => c.charCodeAt(0)); };
const pickBlob = (f) => ({ blob: String(f.blob || ""), key: String(f.key || ""), nonce: String(f.nonce || ""), mime: String(f.mime || "image/jpeg").slice(0, 60) });

async function buildDb(alias, decoyFlag) {
  const SQL = await loadSQL();
  const db = new SQL.Database();
  db.exec(SCHEMA);
  const id = I.newIdentity();
  db.run("INSERT INTO profile VALUES (?,?,?,?,?,?,?,?,?,?)", [crypto.randomUUID(), alias, id.sign_pk, id.sign_sk, id.box_pk, id.box_sk, I.toCode(id.sign_pk, id.box_pk), decoyFlag ? 1 : 0, "deep_orbit", Date.now()]);
  const bytes = db.export();
  db.close();
  return bytes;
}

async function buildRestoredDb(snap, decoyFlag) {
  const SQL = await loadSQL();
  const db = new SQL.Database();
  db.exec(SCHEMA);
  for (const sql of MIGRATIONS) { try { db.exec(sql); } catch { /* already migrated */ } }
  const id = I.identityFromSecrets(snap.p.s, snap.p.b);
  const now = Date.now();
  db.run("INSERT INTO profile (id, alias, sign_pk, sign_sk, box_pk, box_sk, code, decoy_pin_active, theme_state, created_at, push_on) VALUES (?,?,?,?,?,?,?,?,?,?,?)", [crypto.randomUUID(), String(snap.p.a || "Orion").slice(0, 40), id.sign_pk, id.sign_sk, id.box_pk, id.box_sk, I.toCode(id.sign_pk, id.box_pk), decoyFlag ? 1 : 0, "deep_orbit", now, 0]);
  for (const c of snap.c || []) {
    const k = I.parseCode(c.code);
    if (!k) continue;
    db.run("INSERT OR IGNORE INTO contacts (code, alias, nickname, sign_pk, box_pk, routing_id, shared_secret, lock_status, created_at, trust) VALUES (?,?,?,?,?,?,?,?,?,?)", [c.code, String(c.alias || "Unknown").slice(0, 40), c.nickname || null, k.sign_pk, k.box_pk, await I.routingId(k.sign_pk), I.sharedSecret(k.box_pk, id.box_sk), ["connected", "pending", "compromised"].includes(c.lock) ? c.lock : "pending", now, ["accepted", "request", "group", "blocked"].includes(c.trust) ? c.trust : "accepted"]);
  }
  for (const s of snap.s || []) {
    if (!s.id || !["direct", "group"].includes(s.kind)) continue;
    db.run(SESSION_COLS.replace("INSERT", "INSERT OR IGNORE"), [s.id, s.kind, s.name || null, s.rc || null, typeof s.m === "string" ? s.m : "[]", clampTimer(s.t), now, 0, now, Number(s.pd) || 0]);
  }
  const bytes = db.export();
  db.close();
  return bytes;
}

async function readLoginKey(key, pass, label) {
  const bytes = await V.openExport(pass, key);
  let snap = null;
  try { snap = bytes && JSON.parse(new TextDecoder().decode(bytes)); } catch { snap = null; }
  if (!snap || snap.v !== 1 || !snap.p?.s || !snap.p?.b) throw new Error(`${label} key or passphrase is incorrect`);
  return snap;
}

export function VaultProvider({ children }) {
  const [phase, setPhase] = useState("loading");
  const [profile, setProfile] = useState(null);
  const [version, setVersion] = useState(0);
  const [relayStatus, setRelayStatus] = useState("disconnected");
  const [activeSid, setActiveSid] = useState(null);
  const S = useRef({});
  const activeRef = useRef(null);
  activeRef.current = activeSid;

  useEffect(() => { V.vaultExists().then((e) => setPhase(e ? "locked" : "onboard")); }, []);

  const run = (sql, p) => S.current.db.run(sql, p);
  const get1 = (sql, p) => one(S.current.db, sql, p);
  const q = useCallback((sql, p) => (S.current.db ? all(S.current.db, sql, p) : []), []);

  const changed = () => {
    setVersion((v) => v + 1);
    const s = S.current;
    clearTimeout(s.saveTimer);
    s.saveTimer = setTimeout(() => s.db && V.saveSlot(s.slot, s.key, s.db.export()), 300);
  };

  const routeItems = async (codes, body, notify = false) => {
    const s = S.current;
    const items = [];
    for (const code of codes) {
      const c = get1("SELECT * FROM contacts WHERE code=?", [code]);
      if (c) items.push({ routingId: c.routing_id, ...I.sealFor(c.box_pk, { ...body, from: s.me.code, alias: s.me.alias }, s.me), ttl: TTL, ...(notify ? { notify: true } : {}) });
    }
    if (items.length && !s.relay?.send(items)) s.outbox.push(...items);
  };

  const ensureContact = async (code, alias, trust = "accepted") => {
    const ex = get1("SELECT * FROM contacts WHERE code=?", [code]);
    if (ex) {
      if (alias && alias !== ex.alias) run("UPDATE contacts SET alias=? WHERE code=?", [alias, code]);
      return { contact: ex, created: false };
    }
    const k = I.parseCode(code);
    if (!k) return null;
    run("INSERT INTO contacts (code, alias, nickname, sign_pk, box_pk, routing_id, shared_secret, lock_status, created_at, trust) VALUES (?,?,?,?,?,?,?,?,?,?)", [code, alias || "Unknown", null, k.sign_pk, k.box_pk, await I.routingId(k.sign_pk), I.sharedSecret(k.box_pk, S.current.me.box_sk), "pending", Date.now(), trust]);
    return { contact: get1("SELECT * FROM contacts WHERE code=?", [code]), created: true };
  };

  const ensureDirect = async (code, pending = false) => {
    const sid = await I.directSid(S.current.me.code, code);
    if (!get1("SELECT session_id FROM sessions WHERE session_id=?", [sid])) {
      run(SESSION_COLS, [sid, "direct", null, code, "[]", 60, Date.now(), 0, Date.now(), pending ? 1 : 0]);
    }
    return sid;
  };

  const upsertGroup = async (g, pending = false) => {
    const me = S.current.me;
    const members = (g.members || []).filter((m) => I.parseCode(m.code)).slice(0, 64);
    for (const m of members) if (m.code !== me.code && !get1("SELECT code FROM contacts WHERE code=?", [m.code])) await ensureContact(m.code, String(m.alias || "").slice(0, 40), "group");
    const codes = JSON.stringify(members.map((m) => m.code));
    const name = String(g.name || "Group").slice(0, 60);
    if (get1("SELECT session_id FROM sessions WHERE session_id=?", [g.id])) run("UPDATE sessions SET name=?, members=? WHERE session_id=?", [name, codes, g.id]);
    else run(SESSION_COLS, [g.id, "group", name, null, codes, clampTimer(g.timer ?? 60), Date.now(), 0, Date.now(), pending ? 1 : 0]);
  };

  const groupInfo = (sess) => {
    const me = S.current.me;
    const members = JSON.parse(sess.members).map((code) => ({ code, alias: code === me.code ? me.alias : get1("SELECT alias FROM contacts WHERE code=?", [code])?.alias }));
    return { id: sess.session_id, name: sess.name, members, timer: sess.self_destruct_timer };
  };

  const fetchFile = async (id, f, kind = "file") => {
    try {
      const res = await fetch(`${BACKEND}/api/blobs/${f.blob}`);
      if (!res.ok) throw new Error("expired");
      const bytes = I.decryptFile(new Uint8Array(await res.arrayBuffer()), f.key, f.nonce);
      if (!bytes || !S.current.db) return;
      await V.putFile(S.current.key, id, bytes);
      const m = get1("SELECT content, status, session_id FROM messages WHERE message_id=?", [id]);
      if (kind === "snap" && m?.session_id.startsWith("d_")) fetch(`${BACKEND}/api/blobs/${f.blob}`, { method: "DELETE" }).catch(() => {});
      if (!m || m.status === "destroyed") return V.delFile(id);
      run("UPDATE messages SET content=? WHERE message_id=?", [JSON.stringify({ [kind]: { ...f, local: true } }), id]);
      changed();
    } catch {
      if (S.current.db) {
        run("UPDATE messages SET content=? WHERE message_id=?", [JSON.stringify({ [kind]: { ...f, failed: true } }), id]);
        changed();
      }
    }
  };

  const fetchStoryImage = async (id, f) => {
    try {
      const res = await fetch(`${BACKEND}/api/blobs/${f.blob}`);
      if (!res.ok) throw new Error("expired");
      const bytes = I.decryptFile(new Uint8Array(await res.arrayBuffer()), f.key, f.nonce);
      if (!bytes || !S.current.db || !get1("SELECT story_id FROM stories WHERE story_id=?", [id])) return;
      await V.putFile(S.current.key, `s_${id}`, bytes);
      run("UPDATE stories SET content=? WHERE story_id=?", [JSON.stringify({ image: { mime: f.mime, local: true } }), id]);
      changed();
    } catch {
      if (S.current.db) { run("DELETE FROM stories WHERE story_id=?", [id]); changed(); }
    }
  };

  const markRead = async (sid) => {
    const s = S.current;
    if (!s.db) return;
    const now = Date.now();
    const rows = all(s.db, `SELECT message_id, sender_code, timer FROM messages WHERE session_id=? AND sender_code!=? AND read_at IS NULL AND status!='destroyed' AND content NOT LIKE '{"snap"%'`, [sid, s.me.code]);
    const sess = get1("SELECT unread, pending FROM sessions WHERE session_id=?", [sid]);
    if (sess?.pending || (!rows.length && !sess?.unread)) return;
    run("UPDATE sessions SET unread=0 WHERE session_id=?", [sid]);
    const bySender = {};
    for (const m of rows) {
      const burnAt = m.timer > 0 ? now + m.timer * 1000 : null;
      run("UPDATE messages SET read_at=?, burn_at=?, status=? WHERE message_id=?", [now, burnAt, burnAt ? "burning" : "read", m.message_id]);
      (bySender[m.sender_code] ||= []).push({ id: m.message_id, burnAt });
    }
    for (const [code, items] of Object.entries(bySender)) await routeItems([code], { t: "rcpt", kind: "read", items });
    changed();
  };

  const notifyLocal = () => {
    if (!pushSupported() || document.visibilityState === "visible" || Notification.permission !== "granted") return;
    navigator.serviceWorker.ready.then((r) => r.showNotification("Aphelion", { body: "New secure message", icon: "/icon-192.png", tag: "aphelion" })).catch(() => {});
  };

  const processBody = async (b, from, contact) => {
    const me = S.current.me;
    const now = Date.now();
    const trusted = contact.trust === "accepted";
    if (b.t === "hello") { await ensureDirect(from, !trusted); if (trusted) await routeItems([from], { t: "hello_ack" }); else notifyLocal(); return; }
    if (b.t === "hello_ack") { await ensureDirect(from, !trusted); return; }
    const declined = (gid) => get1("SELECT pending FROM sessions WHERE session_id=?", [gid])?.pending === 2;
    if (b.g?.id && declined(b.g.id)) return;
    if (b.t === "ginfo" && b.g?.id?.startsWith("g_")) { await upsertGroup(b.g, !trusted); return; }
    if (b.t === "shot" && b.id) {
      const m = get1("SELECT session_id FROM messages WHERE message_id=? AND sender_code=?", [b.id, me.code]);
      const mid = `shot_${b.id}_${from.slice(4, 12)}`;
      if (m && !get1("SELECT message_id FROM messages WHERE message_id=?", [mid])) {
        run("INSERT INTO messages (message_id, session_id, sender_code, timestamp, content, burn_on_read, timer, status) VALUES (?,?,?,?,?,?,?,?)", [mid, m.session_id, from, now, JSON.stringify({ sys: "screenshot" }), 0, 0, "read"]);
        run("UPDATE messages SET read_at=? WHERE message_id=?", [now, mid]);
      }
      return;
    }
    if (b.t === "cfg") {
      const sid = b.gid || (await I.directSid(me.code, from));
      run("UPDATE sessions SET self_destruct_timer=? WHERE session_id=?", [clampTimer(b.timer), sid]);
      return;
    }
    if (b.t === "msg" && b.id && b.c) {
      let sid;
      if (b.g?.id?.startsWith("g_")) { await upsertGroup(b.g, !trusted); sid = b.g.id; } else sid = await ensureDirect(from, !trusted);
      const pending = !!get1("SELECT pending FROM sessions WHERE session_id=?", [sid])?.pending;
      if (get1("SELECT message_id FROM messages WHERE message_id=?", [b.id])) return;
      const timer = b.c.snap ? clampSnap(b.c.snap.duration) : REACTIONS.includes(b.c.react) ? REACT_TIMER : clampTimer(b.timer);
      const c = b.c.file ? { file: { ...b.c.file, local: false } } : b.c.snap ? { snap: { ...pickBlob(b.c.snap), duration: timer, local: false, reply: !!b.c.snap.reply } } : REACTIONS.includes(b.c.react) ? { react: b.c.react, story: { kind: b.c.story?.kind === "photo" ? "photo" : "text", text: String(b.c.story?.text || "").slice(0, 80) } } : { text: String(b.c.text || "").slice(0, 8000), ...(b.c.story ? { story: { kind: b.c.story.kind === "photo" ? "photo" : "text", text: String(b.c.story.text || "").slice(0, 80) } } : {}) };
      run("INSERT INTO messages (message_id, session_id, sender_code, timestamp, content, burn_on_read, timer, status) VALUES (?,?,?,?,?,?,?,?)", [b.id, sid, from, now, JSON.stringify(c), timer > 0 ? 1 : 0, timer, "delivered"]);
      const viewing = !pending && activeRef.current === sid && document.visibilityState === "visible";
      run("UPDATE sessions SET last_at=?, unread=unread+? WHERE session_id=?", [now, viewing ? 0 : 1, sid]);
      if (!pending) await routeItems([from], { t: "rcpt", kind: "delivered", items: [{ id: b.id }] });
      notifyLocal();
      if (c.file) fetchFile(b.id, c.file);
      if (c.snap) fetchFile(b.id, c.snap, "snap");
      if (viewing) setTimeout(() => markRead(sid), 50);
      return;
    }
    if (b.t === "story" && b.s?.id && trusted) {
      const st = b.s;
      if (get1("SELECT story_id FROM stories WHERE story_id=?", [st.id])) return;
      const ts = Math.min(Number(st.ts) || now, now);
      if (ts + STORY_MS <= now) return;
      const c = st.image ? { image: { ...pickBlob(st.image), local: false } } : { text: String(st.text || "").slice(0, 500), bg: String(st.bg || "#12151C").slice(0, 9) };
      run("INSERT INTO stories VALUES (?,?,?,?,?,?,?)", [st.id, from, ts, ts + STORY_MS, JSON.stringify(c), 0, "[]"]);
      if (c.image) fetchStoryImage(st.id, c.image);
      return;
    }
    if (b.t === "sview" && b.id) {
      const st = get1("SELECT viewed_by FROM stories WHERE story_id=? AND author_code=?", [b.id, me.code]);
      if (!st) return;
      const list = JSON.parse(st.viewed_by);
      if (!list.includes(from)) run("UPDATE stories SET viewed_by=? WHERE story_id=?", [JSON.stringify([...list, from]), b.id]);
      return;
    }
    if (b.t === "rcpt") {
      for (const x of b.items || []) {
        const m = get1("SELECT * FROM messages WHERE message_id=? AND sender_code=?", [x.id, me.code]);
        if (!m) continue;
        const col = b.kind === "read" ? "read_by" : "delivered_to";
        const list = JSON.parse(m[col]);
        if (!list.includes(from)) list.push(from);
        run(`UPDATE messages SET ${col}=? WHERE message_id=?`, [JSON.stringify(list), x.id]);
        if (m.status === "sent") run("UPDATE messages SET status='delivered' WHERE message_id=?", [x.id]);
        if (b.kind === "read" && m.timer > 0 && !m.burn_at && m.status !== "destroyed") {
          const burnAt = Math.max(now, Math.min(Number(x.burnAt) || now + m.timer * 1000, now + m.timer * 1000));
          run("UPDATE messages SET burn_at=?, status='burning' WHERE message_id=?", [burnAt, x.id]);
        }
      }
    }
  };

  const handleItems = async (items) => {
    const s = S.current;
    if (!s.db) return;
    const ackIds = [];
    for (const it of items) {
      ackIds.push(it.id);
      let o = null;
      try { o = I.openSealed(it.encryptedPayload, it.ephemeralSenderKey, s.me); } catch { o = null; }
      if (!o || o.body.from === s.me.code) continue;
      const code = o.body.from;
      if (!o.valid) {
        if (get1("SELECT code FROM contacts WHERE code=?", [code])) run("UPDATE contacts SET lock_status='compromised' WHERE code=?", [code]);
        continue;
      }
      const r = await ensureContact(code, String(o.body.alias || "").slice(0, 40) || null, "request");
      if (!r || r.contact.trust === "blocked") continue;
      if (r.contact.lock_status !== "compromised") run("UPDATE contacts SET lock_status='connected' WHERE code=?", [code]);
      try { await processBody(o.body, code, r.contact); } catch (e) { console.error("process failed", e); }
    }
    s.relay?.ack(ackIds);
    changed();
  };

  const openVault = async ({ slot, key, bytes }) => {
    const SQL = await loadSQL();
    const db = new SQL.Database(bytes);
    db.exec(SCHEMA);
    for (const sql of MIGRATIONS) { try { db.exec(sql); } catch { /* already migrated */ } }
    const me = one(db, "SELECT * FROM profile");
    S.current = { db, slot, key, me, outbox: [], chain: Promise.resolve() };
    const enqueue = (items) => { S.current.chain = S.current.chain.then(() => handleItems(items)).catch(() => {}); };
    S.current.relay = connectRelay(me, {
      onItems: enqueue,
      onStatus: (st) => {
        setRelayStatus(st);
        if (st === "connected" && S.current.outbox?.length) S.current.relay.send(S.current.outbox.splice(0));
        if (st === "connected") syncPush();
      },
    });
    setProfile(me);
    setVersion((v) => v + 1);
    setPhase("unlocked");
  };

  const create = async ({ alias, pass, decoy }) => {
    const realBytes = await buildDb(alias, !!decoy);
    const decoyBytes = decoy ? await buildDb(decoy.alias, true) : null;
    const r = await V.createVaults(pass, realBytes, decoy && { pass: decoy.pass, bytes: decoyBytes });
    await openVault({ slot: r.slot, key: r.key, bytes: realBytes });
  };

  const unlock = async (pass) => {
    const res = await V.unlock(pass);
    if (!res) return false;
    await openVault(res);
    return true;
  };

  const login = async ({ key, pass, decoy }) => {
    const real = await readLoginKey(key, pass, "Login");
    const d = decoy ? await readLoginKey(decoy.key, decoy.pass, "Decoy") : null;
    if (d && decoy.pass === pass) throw new Error("Decoy passphrase must differ from your real one");
    if (d && d.p.s === real.p.s) throw new Error("The decoy key is the same identity as your real key");
    const realBytes = await buildRestoredDb(real, !!d);
    const decoyBytes = d ? await buildRestoredDb(d, true) : null;
    await V.wipeAll();
    const r = await V.createVaults(pass, realBytes, d && { pass: decoy.pass, bytes: decoyBytes });
    await openVault({ slot: r.slot, key: r.key, bytes: realBytes });
  };

  const exportLoginKey = async (pass) => {
    const res = await V.unlock(pass);
    if (!res || res.slot !== S.current.slot) throw new Error("Incorrect passphrase");
    const me = S.current.me;
    const snap = {
      v: 1,
      p: { a: me.alias, s: me.sign_sk, b: me.box_sk },
      c: q("SELECT code, alias, nickname, trust, lock_status FROM contacts").map((c) => ({ code: c.code, alias: c.alias, nickname: c.nickname, trust: c.trust, lock: c.lock_status })),
      s: q("SELECT session_id, kind, name, recipient_code, members, self_destruct_timer, pending FROM sessions").map((s) => ({ id: s.session_id, kind: s.kind, name: s.name, rc: s.recipient_code, m: s.members, t: s.self_destruct_timer, pd: s.pending })),
    };
    return V.sealExport(pass, new TextEncoder().encode(JSON.stringify(snap)));
  };

  const lock = useCallback(async () => {
    const s = S.current;
    s.relay?.close();
    clearTimeout(s.saveTimer);
    if (s.db) {
      await V.saveSlot(s.slot, s.key, s.db.export());
      s.db.close();
    }
    S.current = {};
    setProfile(null);
    setActiveSid(null);
    setRelayStatus("disconnected");
    setPhase("locked");
  }, []);

  const wipe = async () => {
    await disablePush().catch(() => {});
    clearTimeout(S.current.saveTimer);
    S.current.relay?.close();
    S.current.db?.close();
    S.current = {};
    await V.wipeAll();
    setProfile(null);
    setActiveSid(null);
    setPhase("onboard");
  };

  const addContact = async (code, nickname) => {
    const c = code.trim();
    if (c === S.current.me.code) throw new Error("That's your own Aphelion ID");
    if (!I.parseCode(c)) throw new Error("Invalid Aphelion ID");
    await ensureContact(c, null);
    run("UPDATE contacts SET trust='accepted' WHERE code=?", [c]);
    run("UPDATE sessions SET pending=0 WHERE recipient_code=?", [c]);
    if (nickname) run("UPDATE contacts SET nickname=? WHERE code=?", [nickname.slice(0, 40), c]);
    const sid = await ensureDirect(c);
    await routeItems([c], { t: "hello" }, true);
    changed();
    return sid;
  };

  const createGroup = async (name, codes) => {
    const id = "g_" + crypto.randomUUID();
    run(SESSION_COLS, [id, "group", name.slice(0, 60), null, JSON.stringify([S.current.me.code, ...codes]), 60, Date.now(), 0, Date.now(), 0]);
    await routeItems(codes, { t: "ginfo", g: groupInfo(get1("SELECT * FROM sessions WHERE session_id=?", [id])) });
    changed();
    return id;
  };

  const recipientsOf = (sess) => (sess.kind === "direct" ? [sess.recipient_code] : JSON.parse(sess.members).filter((x) => x !== S.current.me.code));

  const uploadSealed = async (bytes, ttl) => {
    const e = I.encryptFile(bytes);
    const res = await fetch(`${BACKEND}/api/blobs?ttl=${ttl}`, { method: "POST", body: e.ct, headers: { "Content-Type": "application/octet-stream" } });
    if (!res.ok) throw new Error("Encrypted upload failed");
    return { blob: (await res.json()).id, key: e.key, nonce: e.nonce };
  };

  const sendMessage = async (sid, { text, file, snap, story, react }) => {
    const s = S.current;
    const sess = get1("SELECT * FROM sessions WHERE session_id=?", [sid]);
    const id = crypto.randomUUID();
    const now = Date.now();
    let c;
    if (file) {
      const bytes = new Uint8Array(await file.arrayBuffer());
      const e = I.encryptFile(bytes);
      const res = await fetch(`${BACKEND}/api/blobs?ttl=${TTL}`, { method: "POST", body: e.ct, headers: { "Content-Type": "application/octet-stream" } });
      if (!res.ok) throw new Error("Encrypted upload failed");
      await V.putFile(s.key, id, bytes);
      c = { file: { blob: (await res.json()).id, key: e.key, nonce: e.nonce, name: file.name.slice(0, 120), mime: file.type || "application/octet-stream", size: file.size } };
    } else if (snap) {
      const duration = clampSnap(snap.duration);
      const up = await uploadSealed(new Uint8Array(await snap.blob.arrayBuffer()), TTL);
      c = { snap: { ...up, mime: snap.blob.type || "image/jpeg", duration, ...(snap.reply ? { reply: true } : {}) } };
    } else if (react) c = { react, story };
    else c = story ? { text, story } : { text };
    const timer = snap ? c.snap.duration : react ? REACT_TIMER : story ? sess.self_destruct_timer || 60 : sess.self_destruct_timer;
    const local = c.file ? { file: { ...c.file, local: true } } : c.snap ? { snap: { mime: c.snap.mime, duration: timer, outgoing: true, reply: !!c.snap.reply } } : c;
    run("INSERT INTO messages (message_id, session_id, sender_code, timestamp, content, burn_on_read, timer, status) VALUES (?,?,?,?,?,?,?,?)", [id, sid, s.me.code, now, JSON.stringify(local), timer > 0 ? 1 : 0, timer, "sent"]);
    run("UPDATE sessions SET last_at=? WHERE session_id=?", [now, sid]);
    const body = { t: "msg", id, c, timer, ts: now };
    if (sess.kind === "group") body.g = groupInfo(sess);
    await routeItems(recipientsOf(sess), body, true);
    changed();
  };

  const replyToStory = async (author, st, text) => {
    const sid = await ensureDirect(author);
    await sendMessage(sid, { text, story: st.content.image ? { kind: "photo" } : { kind: "text", text: st.content.text.slice(0, 80) } });
  };

  const reactToStory = async (author, st, emoji) => {
    const sid = await ensureDirect(author);
    await sendMessage(sid, { react: emoji, story: st.content.image ? { kind: "photo" } : { kind: "text", text: st.content.text.slice(0, 80) } });
  };

  const unblockContact = (code) => {
    run("UPDATE contacts SET trust='request' WHERE code=? AND trust='blocked'", [code]);
    changed();
  };

  const acceptRequest = async (sid) => {
    const sess = get1("SELECT * FROM sessions WHERE session_id=?", [sid]);
    if (!sess) return;
    run("UPDATE sessions SET pending=0 WHERE session_id=?", [sid]);
    if (sess.kind === "direct") {
      run("UPDATE contacts SET trust='accepted' WHERE code=?", [sess.recipient_code]);
      await routeItems([sess.recipient_code], { t: "hello_ack" });
    }
    changed();
  };

  const blockRequest = async (sid) => {
    const sess = get1("SELECT * FROM sessions WHERE session_id=?", [sid]);
    if (!sess) return;
    if (sess.kind === "direct") run("UPDATE contacts SET trust='blocked' WHERE code=?", [sess.recipient_code]);
    else run("UPDATE sessions SET pending=2, unread=0 WHERE session_id=?", [sid]);
    for (const m of all(S.current.db, "SELECT message_id, content FROM messages WHERE session_id=?", [sid])) {
      const c = JSON.parse(m.content || "{}");
      if (c.file || c.snap) V.delFile(m.message_id);
    }
    run("DELETE FROM messages WHERE session_id=?", [sid]);
    if (sess.kind === "direct") run("DELETE FROM sessions WHERE session_id=?", [sid]);
    if (activeRef.current === sid) setActiveSid(null);
    changed();
  };

  const reportScreenshot = async (id) => {
    const m = get1("SELECT sender_code FROM messages WHERE message_id=?", [id]);
    if (m && m.sender_code !== S.current.me.code) await routeItems([m.sender_code], { t: "shot", id }, true);
  };

  const subscribe = async () => {
    const reg = await navigator.serviceWorker.ready;
    const { publicKey } = await (await fetch(`${BACKEND}/api/push/vapid`)).json();
    const sub = (await reg.pushManager.getSubscription()) || (await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: b64ToBytes(publicKey) }));
    S.current.relay?.raw({ type: "push_sub", sub: sub.toJSON() });
  };

  const syncPush = async () => {
    if (!pushSupported() || Notification.permission !== "granted" || !S.current.me?.push_on) return;
    await subscribe().catch(() => {});
  };

  const setPushFlag = (on) => {
    run("UPDATE profile SET push_on=?", [on ? 1 : 0]);
    S.current.me = get1("SELECT * FROM profile");
    setProfile(S.current.me);
    changed();
  };

  const enablePush = async () => {
    if (!pushSupported()) throw new Error("Push isn't supported here");
    if ((await Notification.requestPermission()) !== "granted") throw new Error("Notifications were blocked");
    await subscribe();
    setPushFlag(true);
  };

  const disablePush = async () => {
    S.current.relay?.raw({ type: "push_unsub" });
    if (S.current.db) setPushFlag(false);
    if (!pushSupported()) return;
    const sub = await (await navigator.serviceWorker.ready).pushManager.getSubscription();
    await sub?.unsubscribe();
  };

  const openSnap = async (id) => {
    const m = get1("SELECT * FROM messages WHERE message_id=? AND sender_code!=?", [id, S.current.me.code]);
    if (!m || m.read_at || m.status === "destroyed") return;
    const now = Date.now();
    const burnAt = now + m.timer * 1000;
    run("UPDATE messages SET read_at=?, burn_at=?, status='burning' WHERE message_id=?", [now, burnAt, id]);
    await routeItems([m.sender_code], { t: "rcpt", kind: "read", items: [{ id, burnAt }] });
    changed();
  };

  const postStory = async ({ text, bg, blob }) => {
    const s = S.current;
    const id = crypto.randomUUID();
    const ts = Date.now();
    let wire;
    let local;
    if (blob) {
      const bytes = new Uint8Array(await blob.arrayBuffer());
      const up = await uploadSealed(bytes, 86400);
      await V.putFile(s.key, `s_${id}`, bytes);
      wire = { id, ts, image: { ...up, mime: blob.type || "image/jpeg" } };
      local = { image: { mime: blob.type || "image/jpeg", local: true } };
    } else {
      wire = { id, ts, text: text.slice(0, 500), bg };
      local = { text: wire.text, bg };
    }
    run("INSERT INTO stories VALUES (?,?,?,?,?,?,?)", [id, s.me.code, ts, ts + STORY_MS, JSON.stringify(local), 1, "[]"]);
    const codes = all(s.db, "SELECT c.code FROM contacts c JOIN sessions s ON s.recipient_code=c.code AND s.kind='direct' AND s.pending=0 WHERE c.lock_status!='compromised' AND c.trust='accepted'").map((r) => r.code);
    await routeItems(codes, { t: "story", s: wire }, true);
    changed();
  };

  const viewStory = async (id) => {
    const st = get1("SELECT * FROM stories WHERE story_id=?", [id]);
    if (!st || st.seen) return;
    run("UPDATE stories SET seen=1 WHERE story_id=?", [id]);
    if (st.author_code !== S.current.me.code) await routeItems([st.author_code], { t: "sview", id });
    changed();
  };

  const setTimer = async (sid, seconds) => {
    const sess = get1("SELECT * FROM sessions WHERE session_id=?", [sid]);
    run("UPDATE sessions SET self_destruct_timer=? WHERE session_id=?", [seconds, sid]);
    await routeItems(recipientsOf(sess), { t: "cfg", timer: seconds, ...(sess.kind === "group" ? { gid: sid } : {}) });
    changed();
  };

  const setupDecoy = async (alias, pass) => {
    if (await V.unlock(pass)) throw new Error("Decoy passphrase must differ from your real one");
    const bytes = await buildDb(alias, true);
    await V.writeSlot(1 - S.current.slot, pass, bytes);
    run("UPDATE profile SET decoy_pin_active=1");
    S.current.me = get1("SELECT * FROM profile");
    setProfile(S.current.me);
    changed();
  };

  const getFileBytes = (id) => V.getFile(S.current.key, id);

  useEffect(() => {
    if (phase !== "unlocked") return;
    const t = setInterval(() => {
      const s = S.current;
      if (!s.db) return;
      const now = Date.now();
      const rows = all(s.db, "SELECT message_id, content FROM messages WHERE burn_at IS NOT NULL AND burn_at<=? AND status!='destroyed'", [now]);
      const expired = all(s.db, "SELECT story_id, content FROM stories WHERE expires_at<=?", [now]);
      if (!rows.length && !expired.length) return;
      for (const m of rows) {
        const c = JSON.parse(m.content || "{}");
        if (c.file || c.snap) V.delFile(m.message_id);
        run("UPDATE messages SET content=?, status='destroyed' WHERE message_id=?", [c.snap ? '{"snap":{"gone":true}}' : "{}", m.message_id]);
      }
      for (const st of expired) {
        if (JSON.parse(st.content).image) V.delFile(`s_${st.story_id}`);
        run("DELETE FROM stories WHERE story_id=?", [st.story_id]);
      }
      changed();
    }, 1000);
    let hideTimer;
    const onVis = () => {
      clearTimeout(hideTimer);
      if (document.visibilityState === "hidden") hideTimer = setTimeout(lock, AUTO_LOCK_MS);
      else if (activeRef.current) markRead(activeRef.current);
    };
    document.addEventListener("visibilitychange", onVis);
    return () => { clearInterval(t); clearTimeout(hideTimer); document.removeEventListener("visibilitychange", onVis); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, lock]);

  const value = { phase, profile, version, relayStatus, activeSid, setActiveSid, q, create, unlock, login, exportLoginKey, lock, wipe, addContact, createGroup, sendMessage, markRead, setTimer, setupDecoy, getFileBytes, openSnap, postStory, viewStory, replyToStory, reactToStory, unblockContact, acceptRequest, blockRequest, reportScreenshot, enablePush, disablePush, pushSupported };
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}
